import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { FiLayers, FiMonitor } from 'react-icons/fi';
import { useConfirm } from '../ConfirmDialog';
import { formatApiError } from '../../utils/formatters';
import { TextViewDialog } from './QueryView';

const API_ROOT = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');
const PERF_API = `${API_ROOT}/sql-developer/monitoring/sql-perf`;
// SQL Monitor 는 Tuning Pack 기능이라 처음 한 번은 묻는다(편집기 결과 영역의 「SQL Monitor 리포트」 와 같은 규칙) — 답은 이 브라우저에만 기억한다
const MON_ACK_KEY = 'livesql_sqlmon_ack';
const acked = () => { try { return localStorage.getItem(MON_ACK_KEY) === '1'; } catch (_e) { return false; } };
const ack = () => { try { localStorage.setItem(MON_ACK_KEY, '1'); } catch (_e) { /* 다음에 다시 묻는다 */ } };

/** 서버가 준 사유 코드(sqlPlanView.readPlan 의 notes) → 문구 */
const noteText = (n, t) => {
    const [code, ...rest] = String(n).split(':');
    const msg = rest.join(':');
    switch (code) {
        case 'NOT_IN_POOL': return t('monitoring:planView.note.notInPool');
        case 'NOT_IN_AWR': return t('monitoring:planView.note.notInAwr');
        case 'CURSOR_FAILED': return t('monitoring:planView.note.cursorFailed', { message: msg });
        case 'AWR_FAILED': return t('monitoring:planView.note.awrFailed', { message: msg });
        default: return n;
    }
};

/**
 * 실행 중인 쿼리 · 실행 이력의 「실행계획 보기」 · 「SQL Monitor 리포트 보기」(2026-10-27). 두 탭이 같은 함수·같은 창을 쓴다.
 * run/dbTarget 은 그 화면의 것(창의 세션으로 묻는다).
 */
export default function useSqlPlanViewer({ run, dbTarget, t }) {
    const confirm = useConfirm();
    const [view, setView] = useState(null);   // { kind, title, loading, text, error, meta, notes }

    const openPlan = useCallback(async ({ sqlId, instId, planHash, source }) => {
        if (!sqlId) return;
        const title = t('monitoring:planView.planTitle', { sqlId });
        setView({ kind: 'plan', title, loading: true });
        try {
            const r = await run(() => axios.get(`${PERF_API}/plan`, {
                params: { ...dbTarget, sqlId, instId: instId ?? undefined, planHash: planHash ?? undefined, source: source || undefined },
            }));
            const d = r.data || {};
            const meta = d.source === 'cursor'
                ? t('monitoring:planView.fromCursor', { inst: d.instId ?? '-', child: d.childNumber ?? '-', hash: d.planHash ?? '-' })
                    + (d.running ? ` · ${t('monitoring:planView.runningChild')}` : '')
                : d.source === 'awr' ? t('monitoring:planView.fromAwr', { hash: d.planHash ?? t('monitoring:planView.allPlans') }) : null;
            setView({ kind: 'plan', title, text: d.text, meta, notes: (d.notes || []).map(n => noteText(n, t)) });
        } catch (err) {
            setView({ kind: 'plan', title, error: formatApiError(err, t) });
        }
    }, [run, dbTarget, t]);

    const openMonitor = useCallback(async ({ sqlId, instId, sqlExecId, sqlExecStart }) => {
        if (!sqlId) return;
        if (!acked()) {
            const ok = await confirm({
                title: t('monitoring:planView.monAskTitle'),
                message: t('monitoring:planView.monAsk'),
                confirmText: t('monitoring:planView.monAskOk'),
            });
            if (!ok) return;
            ack();
        }
        const title = t('monitoring:planView.monTitle', { sqlId });
        setView({ kind: 'monitor', title, loading: true });
        try {
            const r = await run(() => axios.get(`${PERF_API}/monitor-report`, {
                params: { ...dbTarget, sqlId, instId: instId ?? undefined, sqlExecId: sqlExecId ?? undefined, sqlExecStart: sqlExecStart ?? undefined },
            }));
            const d = r.data || {};
            setView({ kind: 'monitor', title, text: d.text,
                meta: d.pinned ? t('monitoring:planView.monPinned', { execId: sqlExecId, start: sqlExecStart }) : t('monitoring:planView.monLatest'),
                notes: d.text ? [] : [t('monitoring:planView.note.noMonitor')] });
        } catch (err) {
            setView({ kind: 'monitor', title, error: formatApiError(err, t) });
        }
    }, [run, dbTarget, t, confirm]);

    const dialog = view ? (
        <TextViewDialog
            title={view.title}
            icon={view.kind === 'monitor' ? <FiMonitor size={14} /> : <FiLayers size={14} />}
            text={view.text} loading={view.loading} error={view.error} meta={view.meta} notes={view.notes}
            onClose={() => setView(null)}
        />
    ) : null;

    return { openPlan, openMonitor, dialog };
}
